import { FieldBaseProps, SubmitGroup, ValidityGroup, useInputDisabled, useInputReadonly } from "capitalrx-components-2.0/lib/forms";
import { SharedProps } from "capitalrx-components-2.0/lib/forms/Textbox/SharedProps";
import {
  DisabledLengthRule,
  DisabledRequiredRule,
  length as lengthRule,
  required as requiredRule,
} from "capitalrx-components-2.0/lib/forms/validation";
import { DataId } from "capitalrx-components-2.0/lib/general";
import { FC, useCallback, useEffect, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { Input } from "./Input";
import { SharedTypeaheadTextboxProps } from "./Props";

/**
 * The `Field` component properties.
 */

export interface FieldProps<TRow extends Object, TMetadata extends object>
  extends FieldBaseProps<string | null>,
    SharedProps,
    SharedTypeaheadTextboxProps<TRow, TMetadata> {
  /**
   * The minimum number of characters allowed.
   */
  readonly minLength?: number;
}

/**
 * A React Hook Form compatible Textbox Field component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps<any, any>> = (props) => {
  const {
    dataSource,
    openOnFocus,
    name,
    required = false,
    minLength,
    maxLength,
    defaultValue = null,
    validator,
    ...restProps
  } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const rules = useMemo(
    () => ({
      ...(disabled || readOnly ? DisabledRequiredRule : requiredRule(required)),
      ...(disabled || readOnly ? DisabledLengthRule : lengthRule(minLength, maxLength)),
      validate: disabled || readOnly ? undefined : validator,
    }),
    [disabled, readOnly, required, minLength, maxLength, validator],
  );

  const { resetField, setValue } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });
  const { ref, value, onChange, onBlur } = field;

  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  const onItemSelected = useCallback(
    (dataId: DataId) => {
      const selectedRow = dataSource.lookup(dataId);
      setValue(name, selectedRow.value);
    },
    [setValue, dataSource],
  );

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...restProps}
          ref={ref}
          value={value}
          type="text"
          required={required}
          maxLength={maxLength}
          defaultValue={defaultValue}
          onChange={onChange}
          onItemSelected={onItemSelected}
          onFocusExit={onBlur}
          dataSource={dataSource}
          openOnFocus={openOnFocus}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
